// src/config/guideConfig.js

/**
 * Central configuration for all in-app guides and coach marks.
 *
 * Each guide has:
 *   showWhen:    Array of { actionId, minCount } conditions. ALL must be met for
 *               the guide to be eligible. Empty array = no conditions (always eligible).
 *
 *   completedBy: The actionId that represents "the user has done this thing."
 *               On the first time that action is incremented, this guide is
 *               automatically hard-dismissed — so users who already know the
 *               feature never see the guide. Also suppresses the guide while
 *               count > 0, as a safety fallback.
 *
 * Action counts (actionCounts in Firestore) replace milestones. Every tracked
 * action is incremented via incrementActionCount() from useGuide.js.
 *
 * To add a new guide:
 *   1. Add an entry here.
 *   2. Call useGuide('yourGuideId', { localCondition }) in the host component.
 *   3. Add incrementActionCount(uid, 'yourActionId') wherever the action happens.
 *   4. For case-level conditions, compute them locally and pass as localCondition.
 */

export const GUIDE_CONFIG = {

  // ─── Low-level guides (Got it only) ──────────────────────────────────────
  // These teach a single discoverable mechanic. Once the user does it once,
  // the guide pre-dismisses and never appears again.

  // ─── High-level guide (Got it + Later, multi-page) ───────────────────────
  // Introduces bulk actions via a 3-step walkthrough that auto-advances
  // as the user performs each action.

  bulkTagGuide: {
    // Show after the user's first export — they've completed a full usage cycle
    // and are ready for bulk actions. Toolbar appears when any docs are selected
    // (localCondition in EvidenceManager). Anchor: checkboxes on first 3 rows
    // (step 1), Add Tags button (step 2).
    // bulkTagGuideInteracted is incremented on Close or Got it — first interaction
    // permanently suppresses auto-appearance via the completedBy auto-dismiss.
    showWhen: [
      { actionId: 'exportDocs', minCount: 1 },
    ],
    completedBy: ['bulkTag', 'bulkDelete', 'bulkRemoveTag', 'bulkTagGuideInteracted'],
  },

};


/**
 * Every action we track with an actionCount in Firestore.
 * Includes actions that have guide triggers AND actions tracked for
 * analytics purposes only (no associated guide).
 *
 * Used by GuideDevPanel to display and control all counts.
 */
export const ALL_TRACKED_ACTIONS = [
  'createCase',
  'uploadDoc',               // incremented once per doc, regardless of batch size
  'uploadMultipleDocs',      // incremented once per batch when > 1 doc uploaded at once
  'docMadeSearchable',
  'docConvertedToPdf',
  'reorderDocs',
  'moveStamp',
  'searchForDoc',
  'createTag',               // any tag creation, any source
  'createTagFromExportModal',
  'createTagInMainFlow',     // tag created from sidebar or PDF table row (not export modal)
  'addTagToSingleDoc',
  'uploadDocsToTag',
  'bulkDelete',
  'bulkTag',
  'bulkRemoveTag',
  'bulkTagGuideInteracted',
  'archiveTag',
  'archiveCase',
  'restoreTag',
  'restoreCase',
  'exportDocs',
  'tagGuideOpened',          // incremented once each time the TagGuide is opened (not per page)
];